import { cn } from '@/lib/utils'
import { PanelBody } from './panel-body'
import { VerticalTab } from './vertical-tab'
import { HorizontalTab } from './horizontal-tab'

interface Tab<T extends string = string> {
  id: T
  label: string
  /**
   * The plan does not include this tab's feature. It still renders and still
   * fires `onTabChange`, marked with a lock: a disabled tab would swallow the
   * click, and the click is what raises the upgrade pitch. The owner decides
   * what a locked click does, and is responsible for not switching the body.
   */
  locked?: boolean
}

interface TabbedPanelProps<T extends string = string> {
  /** Tab definitions — order determines render order in the tab strip. */
  tabs: readonly Tab<T>[]
  /** Currently active tab id. */
  activeTab: T
  /** Called when a tab is clicked. */
  onTabChange: (tabId: T) => void
  /** Loading overlay on the content area. */
  loading?: boolean
  transparentOverlay?: boolean
  /** Position of the tab strip. `top` renders a horizontal strip above the body. */
  tabPosition?: 'left' | 'right' | 'top'
  className?: string
  /**
   * Tab content. Receives the active tab id.
   * All children are always mounted — inactive tabs are hidden via CSS
   * to preserve scroll position and component state.
   */
  children: React.ReactNode
  /** Pinned content rendered above the tab strip and body (e.g. a price header). */
  header?: React.ReactNode
  /** Extra content rendered outside the body (e.g. Sheet dialogs). */
  extra?: React.ReactNode
}

/**
 * Panel with a tab strip. Vertical (left/right) or horizontal (top).
 * Mirrors the old MosaicPanelTabbedBody pattern: content area + tab strip
 * with consistent layout, loading overlay, and keyboard navigation.
 */
export function TabbedPanel<T extends string = string>({
  tabs,
  activeTab,
  onTabChange,
  loading,
  transparentOverlay,
  tabPosition = 'right',
  className,
  children,
  header,
  extra,
}: TabbedPanelProps<T>) {
  const horizontal = tabPosition === 'top'

  const strip = horizontal ? (
    <div
      // overflow-y-hidden is load-bearing: setting overflow-x to a non-visible
      // value (auto) makes CSS compute overflow-y from `visible` to `auto` too,
      // which paints a stray vertical scrollbar on the tab strip. Pin y to hidden.
      className="flex flex-row gap-1 shrink-0 overflow-x-auto overflow-y-hidden px-2 border-b border-border-strong"
      role="tablist"
      aria-orientation="horizontal"
    >
      {tabs.map((tab) => (
        <HorizontalTab
          key={tab.id}
          label={tab.label}
          active={activeTab === tab.id}
          locked={tab.locked}
          onClick={() => onTabChange(tab.id)}
        />
      ))}
    </div>
  ) : (
    <div
      className={cn(
        'flex flex-col gap-px bg-muted shrink-0 p-0.5',
        tabPosition === 'right' ? 'border-l border-border' : 'border-r border-border',
      )}
      role="tablist"
      aria-orientation="vertical"
    >
      {tabs.map((tab) => (
        <VerticalTab
          key={tab.id}
          label={tab.label}
          active={activeTab === tab.id}
          locked={tab.locked}
          onClick={() => onTabChange(tab.id)}
        />
      ))}
    </div>
  )

  return (
    // No own background — the panel body shares the mosaic window's surface
    // so the panel header and body read as one card.
    <div className={cn('flex flex-col h-full', className)}>
      {header}
      {horizontal && strip}
      <div className={cn('flex flex-1 min-h-0', tabPosition === 'left' && 'flex-row-reverse')}>
        <PanelBody loading={loading} transparentOverlay={transparentOverlay}>
          {children}
        </PanelBody>
        {!horizontal && strip}
      </div>
      {extra}
    </div>
  )
}
